/**
 * How a measured value stands against the limits of its datapoint: the lower
 * and upper alarm (`lat`, `uat`) and warning (`lwt`, `uwt`) thresholds that a
 * wizard hangs on a measurement. The panel colours values by the same bands —
 * alarm beyond the alarm limits, warning between an alarm and its warning
 * limit, fine in between.
 *
 * Only the limits that exist are judged; a value without limits is not judged
 * at all.
 */

export type ThresholdLevel = 'none' | 'ok' | 'warning' | 'alarm'

export interface Limits {
  readonly lat?: number
  readonly lwt?: number
  readonly uwt?: number
  readonly uat?: number
}

export function thresholdLevel(value: number | undefined, limits: Limits): ThresholdLevel {
  if (value === undefined || !Number.isFinite(value)) return 'none'
  const { lat, lwt, uwt, uat } = limits
  if ([lat, lwt, uwt, uat].every((limit) => limit === undefined)) return 'none'

  if (lat !== undefined && value < lat) return 'alarm'
  if (uat !== undefined && value > uat) return 'alarm'
  // Warning limits the wrong way round would put every value in a warning band.
  if (lwt !== undefined && uwt !== undefined && uwt < lwt) return 'ok'
  if (lwt !== undefined && value < lwt) return 'warning'
  if (uwt !== undefined && value > uwt) return 'warning'
  return 'ok'
}

/** The text colour of a level, as Tailwind classes; `fallback` for a value that is not judged. */
export function thresholdTextClass(level: ThresholdLevel, fallback = 'text-slate-800'): string {
  switch (level) {
    case 'alarm':
      return 'text-orange-600'
    case 'warning':
      return 'text-amber-500'
    case 'ok':
      return 'text-green-600'
    default:
      return fallback
  }
}
